import { ArrowRight, ArrowLeft } from '@carbon/icons-react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination, A11y, Mousewheel } from 'swiper'
import { Tile } from 'carbon-components-react'
import { useCallback, useState, useEffect, useRef } from 'react'
import watsonAssistant from './utils/watson-assistant'
import cx from 'classnames'

const ContentCarouselNav = ({ carouselData }) => {
  const instance = watsonAssistant.getInstance()
  const [navigationElement, setNavigationElement] = useState() // Ref for Swiper navigation
  const [carouselItems, setCarouselItems] = useState([])
  const [loading, setLoading] = useState(true)

  const swiperRef = useRef(null)

  useEffect(() => {
    const initialiseCarousel = () => {
      if (Array.isArray(carouselData)) {
        const updatedItems = carouselData.map((item) => {
          const isVideo = item.url.includes('mp4')
          return { ...item, isVideo }
        })
        setCarouselItems(updatedItems)
        setLoading(false)
      } else {
        console.error('No valid carousel_data found in carouselData')
      }
    }

    initialiseCarousel()
  }, [carouselData])

  // Handles click events on images
  const onImageClick = useCallback(
    (tellme) => {
      instance.sendMessageSilent(tellme)
    },
    [instance]
  )

  const spinnerStyle = {
    border: '3px solid rgba(255, 255, 255, 0.3)',
    borderTop: '3px solid #ffffff',
    borderRadius: '50%',
    width: '30px',
    height: '30px',
    animation: 'spin 1.5s linear infinite',
  }

  // Style for the overlay covering the carousel during loading
  const spinnerOverlayStyle = {
    position: 'absolute',
    top: '5px',
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'left',
    justifyContent: 'left',
    zIndex: 10,
  }

  return (
    <div
      className="ChatContainer"
      style={{ position: 'relative' }}
    >
      {loading && (
        <div style={spinnerOverlayStyle}>
          <div style={spinnerStyle}></div>
        </div>
      )}
      {!loading && carouselItems.length > 0 && (
        <div>
          <Swiper
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            modules={[Navigation, Pagination, A11y, Mousewheel]}
            keyboard={{ enabled: true }}
            mousewheel={{
              forceToAxis: true,
              sensitivity: 0.5,
              thresholdDelta: 50,
              thresholdTime: 200,
            }}
            touchEventsTarget="container"
            touchRatio={0.5}
            resistance={true}
            resistanceRatio={1}
            pagination={{
              el: navigationElement?.querySelector('.Carousel__BulletContainer') || null,
              clickable: true,
              bulletClass: 'Carousel__Bullet',
              bulletActiveClass: 'Carousel__Bullet--selected',
              renderBullet,
            }}
            navigation={{
              prevEl: '.Nav__Arrow--Left',
              nextEl: '.Nav__Arrow--right',
            }}
            slidesPerView="auto"
            spaceBetween={15}
            centeredSlides
          >
            {carouselItems.map((cardData) => {
              const { url, title, alt, tellme, isVideo } = cardData

              return (
                <SwiperSlide key={`${url}-${title}`}>
                  <Tile
                    className="Nav_Carousel__Card"
                    style={{ position: 'relative' }}
                  >
                    {isVideo ? (
                      <div className="Nav_Carousel__VideoContainer">
                        <video
                          className="Nav_Carousel__Video"
                          style={{
                            width: '241px',
                            height: '302px',
                            objectFit: 'cover',
                          }}
                          onClick={() => onImageClick(tellme)}
                          disablePictureInPicture
                          controlsList="nodownload nofullscreen noremoteplayback"
                          autoPlay
                          muted
                          loop
                        >
                          <source
                            src={url}
                            type="video/mp4"
                          />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    ) : (
                      <img
                        className="Nav_Carousel__CardImage"
                        src={url}
                        alt={alt}
                        onClick={() => onImageClick(tellme)}
                      />
                    )}
                    <div className="Nav_Carousel__CardContent">
                      <p className="Nav_Carousel__CardTitle_Static">{title}</p>
                    </div>
                  </Tile>
                </SwiperSlide>
              )
            })}
          </Swiper>

          <div
            ref={setNavigationElement}
            className={cx('Carousel__Navigation', {
              'Carousel__Navigation--hidden': !carouselItems,
            })}
          >
            <div className="Carousel__BulletContainer" />
          </div>

          {/* Left Arrow */}
          <button className="Nav__Arrow Nav__Arrow--Left">
            <ArrowLeft size={24} />
          </button>

          {/* Right Arrow */}
          <button className="Nav__Arrow Nav__Arrow--right">
            <ArrowRight size={24} />
          </button>
        </div>
      )}
    </div>
  )
}

// Renders custom bullets for Swiper pagination
function renderBullet(_, className) {
  return `<span class="${className}"></span>`
}

export { ContentCarouselNav }
